import React, { MutableRefObject, useEffect, useRef } from 'react'
import { checkmarkColorClasses, Container, getCheckmarkClasses, OuterPadding } from '@styles/global/helpers'
import { ICourse, ICourse2 } from '@et/types/Courses'
import styled from 'styled-components'
import { device } from '@styles/global/breakpoints'
import ThemeButton from '../../buttons/ThemeButton'
import { svgs } from '@svg'
import CourseCardPrice from '@components/sections/courseCard/price'
import VideoStats from '@components/sections/courseCard/videoStats'
import { shadowStyles } from '@styles/global/shadows'
import { scrollPageTo } from '@utils/genUtils'
import { IGatsbyChildImageSharp, IGatsbyImageDataObject } from '@et/types/Gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { GATSBYIMGLOADING } from '@et/types/Enums'
import BackgroundImageV5 from '@components/imageModules/v5/backgroundImg_v5'

interface IBrushImg {
  primary: IGatsbyImageDataObject
  shadow: IGatsbyImageDataObject
}

interface IProps {
  backgroundGatsbyImg?: IGatsbyImageDataObject | null
  titleImg: IGatsbyImageDataObject
  brush?: IBrushImg | null
  course: ICourse2
  buttonOutlineColor?: string
  buttonHoverColor?: string
  customCheckMarkColors?: string[]
  customBgColor?: string
  imgSize?: string

}

function CourseCardLayoutV4(props: IProps) {
  const { backgroundGatsbyImg, titleImg, brush, course: { courseCard, curriculum }, customCheckMarkColors, imgSize = 'small' } = props
  const courseCardMini: MutableRefObject<null | HTMLElement> = useRef(null)

  useEffect(() => {
    courseCardMini.current = document.getElementById('mini-card')
  }, [])

  function buttonClick() {
    scrollPageTo(courseCardMini)
  }

  return (
    <BackgroundImageV5
      gatsbyDataImg={backgroundGatsbyImg ? backgroundGatsbyImg.gatsbyImageData : null}
      containerStyles={{
        backgroundColor: courseCard.backgroundColor
      }}
    >
      <OuterPadding backgroundColor={`transparent`}>
        <Container>
          <CardContainer>
            {brush &&
              <PencilImageWrapper>
                <GatsbyImage alt={'EveryTuesday Art tools'} image={brush.primary.gatsbyImageData}
                  loading={GATSBYIMGLOADING.LAZY} />
              </PencilImageWrapper>
            }
            <SellCardLarge customBgColor={props.customBgColor}>
              <SellCardInner>
                <Content>
                  <TitleImgWrapper imgSize={imgSize}>
                    <GatsbyImage alt={courseCard.title} image={titleImg.gatsbyImageData}
                      loading={GATSBYIMGLOADING.LAZY} />
                  </TitleImgWrapper>
                  <h2>
                    {courseCard.title}
                  </h2>
                  {brush &&
                    <PencilImageReflectionWrapper>
                      <GatsbyImage alt={courseCard.title} image={brush.shadow.gatsbyImageData}
                        loading={GATSBYIMGLOADING.LAZY} />
                    </PencilImageReflectionWrapper>
                  }
                  <CourseCardPrice {...props.course} />
                  <CardText>
                    {courseCard.desc}
                  </CardText>
                  <VideoStats colors={courseCard.stats} curriculum={curriculum} />
                  <DualColList>
                    {courseCard.bullets.map((bullet: string, index: number) => (
                      <li key={index}>
                        <Icon
                          className={checkmarkColorClasses[index]}>
                          <svg viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                            <path
                              fillRule='evenodd'
                              clipRule='evenodd'
                              style={customCheckMarkColors
                                ? { fill: customCheckMarkColors[index] }
                                : {}}
                              d='M0 9C0 4.032 4.032 0 9 0C13.968 0 18 4.032 18 9C18 13.968 13.968 18 9 18C4.032 18 0 13.968 0 9ZM12.9994 4.48987L14.9777 6.58328L7.04698 15.0101L2.51758 10.2172L4.50603 8.1279L7.04607 10.8157L12.9994 4.48987Z'
                            />
                          </svg>

                        </Icon>
                        <BulletText>{bullet}</BulletText>
                      </li>
                    ))}
                  </DualColList>
                  <ThemeButton
                    icon={svgs.Cart}
                    outlineColor={props.buttonOutlineColor}
                    outlineHoverColor={props.buttonHoverColor}
                    text={courseCard.buttonText ? courseCard.buttonText : `Enroll Now!`}
                    outline={true}
                    clickHandler={buttonClick} />
                </Content>
              </SellCardInner>
            </SellCardLarge>
          </CardContainer>
        </Container>
      </OuterPadding>
    </BackgroundImageV5>
  )
}

const PencilImageWrapper = styled.div`
  position: absolute;
  top: 150px;
  z-index: 5;
  transform-origin: left center;
  transform: rotate(-45deg);
  left: 70%;
  max-width: 600px;
  width: 100%;
  
  @media ${device.tablet}{
    top: 200px;
    left: 60%;
  }
`
const PencilImageReflectionWrapper = styled.div`
  position: absolute;
  top: 140px;
  left: 70%;
  z-index: 1;
  transform-origin: left center;
  transform: rotate(45deg);
  max-width: 600px;
  width: 100%;
  @media ${device.tablet}{
    top: 191px;
    left: 60%;
  }
`
const CardContainer = styled.div`
  position: relative;
  
  @media ${device.tablet}{
    margin: 0 7%;
  }
  
  @media ${device.laptop}{
    margin: 0 2%;
  }
  
    @media ${device.laptopL}{
    margin: 0 7%;
  }
`
const SellCardLarge = styled.div<{ customBgColor: string | undefined }>`
  background: ${props => props.customBgColor ? props.customBgColor : props.theme.backgroundSecondary};
  text-align: center;
  border-radius: 15px;
  box-shadow: ${shadowStyles.shadow1};
`
const SellCardInner = styled.div`
  border-radius: 15px;
  overflow: hidden;
  position: relative;
`
const TitleImgWrapper = styled.div<{ imgSize: string }>`
  width: 100%;
  max-width: ${props => props.imgSize === 'large' ? '750px' : '350px'};
  margin: 0 auto;
`
const CardText = styled.p`
  color: ${props => props.theme.textColor};
  margin-bottom: 30px;
  font-size: 18px;
  font-weight: 500;
  z-index: 2;
  line-height: 30px;
  padding: 0 10px;
  @media ${device.tablet}{
    padding: 0 35px;
  }  
  @media ${device.laptop}{
    padding: 0 25px;
  }
`
const Content = styled.div`
  z-index: 2;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10% 15px;
  h2{
    color: ${props => props.theme.headlineColor};
    font-size: 42px;
    font-weight: 600;
    position: relative;
    line-height: 42px;
    z-index: 2;
  }
  
  @media ${device.tablet}{
    padding: 5% 5% 10%;
  }  
  @media ${device.laptop}{
    padding: 5% 10% 8%;
  }
  @media ${device.laptopL}{
    padding: 5% 12% 8%;
  }

`
const Icon = styled.span`
  position: absolute;
  left: 0;
  top: 13px;
  margin-right: 10px;
  width: 15px;
  height: 15px;
  svg{
    width: 100%;
  }
  
  ${getCheckmarkClasses()};

`
const BulletText = styled.span`
  padding: 10px 20px 0;
  font-size: 16px;
  display: flex;
  flex-direction: column;
`
const DualColList = styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0 0 40px 0;
  padding: 0 5%;

  li{
    position: relative;
    flex: 0 0;
    padding: 0 0 15px;
    text-align: left;
    display: flex;
    flex-direction: column;
    color: ${props => props.theme.textColor};
  }
  
  @media ${device.tablet}{
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 0 0 40px;
    li{
      flex: 0 0 50%;
    }
  }
  
  @media ${device.laptop}{
    padding: 0 0 0 60px;
    margin-left: 5%;
  }
  
`
export default CourseCardLayoutV4
